/**
 * Mode config validation + preset logic.
 * Pure functions — no side effects, no rendering.
 *
 * Validation runs per layer, so a custom combination (say Dominion plus
 * Blackout) gets every layer's checks.
 */

import type { GameMode, ModeConfig, ConfigWarning } from '../types';
import { getDefaultModeConfig } from './modes';
import { getModeRules } from './modeRules';
import { MODE_PRESET_DESCRIPTIONS } from '../constants';

// =============================================================================
// VALIDATION
// =============================================================================

export function validateModeConfig(
  modeOrConfig: GameMode | ModeConfig,
  numTeams: number,
  numPlanets: number,
  ruinDensity: string,
): ConfigWarning[] {
  const config = typeof modeOrConfig === 'string' ? getDefaultModeConfig(modeOrConfig) : modeOrConfig;
  const rules = getModeRules(config);
  const warnings: ConfigWarning[] = [];

  switch (rules.objective) {
    case 'ctf':
      // Needs an enemy core to steal
      if (numTeams < 2) {
        warnings.push({ field: 'numTeams', message: 'Core Retrieval requires at least 2 teams.', severity: 'block' });
      }
      break;
    case 'stakes':
      // Enough planets for meaningful territory
      if (numPlanets < 3) {
        const label = config.multiStake ? 'Territory Survey' : 'Sovereign Claim';
        warnings.push({ field: 'numPlanets', message: `${label} requires at least 3 planets.`, severity: 'block' });
      }
      break;
    case 'signalTrace': {
      const postsNeeded = config.postsToTriangulate ?? 3;
      if (numPlanets < postsNeeded + 1) {
        warnings.push({
          field: 'numPlanets',
          message: `Signal Trace needs at least ${postsNeeded + 1} planets for post placement.`,
          severity: 'warn',
        });
      }
      break;
    }
    case 'uplink':
      // Fragments spawn at ruins
      if (ruinDensity === 'none') {
        warnings.push({
          field: 'ruinDensity',
          message: 'Data Uplink benefits from ruin structures for fragment spawning.',
          severity: 'warn',
        });
      }
      break;
    case 'salvage':
      // Scoring is comparative
      if (numTeams < 2) {
        warnings.push({ field: 'numTeams', message: 'Salvage Recovery requires at least 2 teams.', severity: 'block' });
      }
      break;
    case 'excavation':
    case 'none':
      break;
  }

  for (const env of rules.environments) {
    if (env === 'blackout' && numPlanets < 4) {
      warnings.push({ field: 'numPlanets', message: 'Blackout Protocol plays best with at least 4 planets.', severity: 'warn' });
    }
    if (env === 'lastLight') {
      if (numPlanets < 4) {
        warnings.push({ field: 'numPlanets', message: 'Last Light Contingency plays best with at least 4 planets.', severity: 'warn' });
      }
      const finalCount = config.finalPlanetsCount ?? 1;
      if (finalCount >= numPlanets) {
        warnings.push({ field: 'finalPlanetsCount', message: 'Final planets count must be less than total planets.', severity: 'block' });
      }
    }
  }

  // Objective matches should have turn limits
  if (rules.objective !== 'none' && !config.turnLimit) {
    warnings.push({ field: 'turnLimit', message: 'Objective modes are recommended to have a turn limit.', severity: 'warn' });
  }

  return warnings;
}

/** Team and world counts that cannot or should not go together. */
export function validateTeamSetup(
  numTeams: number,
  botTeams: number,
  planetSize: string,
  numPlanets: number,
): ConfigWarning[] {
  const warnings: ConfigWarning[] = [];
  if (botTeams > numTeams) {
    warnings.push({ field: 'botTeams', message: `More bot teams (${botTeams}) than teams (${numTeams}).`, severity: 'block' });
  } else if (botTeams === numTeams) {
    warnings.push({ field: 'botTeams', message: 'No human team — a bots-only match you will watch.', severity: 'warn' });
  }
  if (planetSize === 'megaplanet' && numPlanets > 2) {
    warnings.push({ field: 'numPlanets', message: 'Megaplanets are enormous; more than 2 will crowd the system.', severity: 'warn' });
  }
  return warnings;
}

// =============================================================================
// PRESETS
// =============================================================================

export interface ModePreset {
  modeConfig: ModeConfig;
  suggestedTeams: number;
  suggestedPlanets: number;
  suggestedTurnLimit: number | undefined;
  suggestedRuinDensity: string;
  description: string;
}

export function getModePreset(mode: GameMode): ModePreset {
  const modeConfig = getDefaultModeConfig(mode);
  const description = MODE_PRESET_DESCRIPTIONS[mode];

  switch (mode) {
    case 'dominion':
      return { modeConfig, suggestedTeams: 3, suggestedPlanets: 8, suggestedTurnLimit: 60, suggestedRuinDensity: 'some', description };
    case 'sovereignty':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 6, suggestedTurnLimit: 60, suggestedRuinDensity: 'some', description };
    case 'ctf':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 6, suggestedTurnLimit: 40, suggestedRuinDensity: 'some', description };
    case 'signalTrace':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 6, suggestedTurnLimit: 60, suggestedRuinDensity: 'some', description };
    case 'uplink':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 8, suggestedTurnLimit: 40, suggestedRuinDensity: 'many', description };
    case 'salvageRun':
      return { modeConfig, suggestedTeams: 3, suggestedPlanets: 6, suggestedTurnLimit: 40, suggestedRuinDensity: 'some', description };
    case 'excavation':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 6, suggestedTurnLimit: 40, suggestedRuinDensity: 'some', description };
    case 'blackout':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 8, suggestedTurnLimit: 80, suggestedRuinDensity: 'some', description };
    case 'lastLight':
      return { modeConfig, suggestedTeams: 2, suggestedPlanets: 10, suggestedTurnLimit: 60, suggestedRuinDensity: 'some', description };
    default: // deathmatch
      return { modeConfig, suggestedTeams: 4, suggestedPlanets: 8, suggestedTurnLimit: undefined, suggestedRuinDensity: 'some', description };
  }
}

// =============================================================================
// HELPERS
// =============================================================================

export function hasBlockingWarnings(warnings: ConfigWarning[]): boolean {
  return warnings.some(w => w.severity === 'block');
}
